import classNames from 'classnames';
import React, {
  FC,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

// Components
import DataPagination from './components/DataPagination';
import LoadingRows from './components/LoadingRows';
import ManageColumnModal from './components/ManageColumnModal';
import TableControls from './components/TableControls';
import TableHeader from './components/TableHeader';
import TableRows from './components/TableRows';

// Hooks and utilities
import { useTranslation } from '../../hooks/useTranslation';
import { pushQueries, removeQueries } from '../../utils';
import {
  ORDER_KEY,
  PAGE_KEY,
  PAGE_SIZE_KEY,
  SORT_KEY,
} from '../../utils/const';
import OffcanvasFilterPanel from './components/OffcanvasFilterPanel';
import { useDataProcessing } from './hooks/useDataProcessing';
import { useStickyOffsets } from './hooks/useStickyOffsets';
import * as styles from './styles.module.scss';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_REFRESH_COUNTDOWN,
  LOCAL_STORAGE_KEYS,
} from './utils/constants';
import {
  handleApplyFilter,
  handleReorderColumns,
  handleSort,
  handleToggleColumn,
  loadColumnOrder,
  loadColumnVisibility,
} from './utils/handlers';
import {
  DataItem,
  DataTableColumn,
  DataTableRef,
  FetchDataFn,
  FilterItem,
  SortConfig,
} from './utils/types';

declare var bootstrap: any;

export interface Pagination {
  pageKey: string;
  pageSizeKey: string;
}

interface DataTableProps {
  isLoading?: boolean;
  columns: DataTableColumn<any>[];
  data: DataItem[];
  tableId: string;
  tfoot?: React.ReactNode[];
  enableFilter?: boolean;
  enableManageColumn?: boolean;
  enablePagination?: boolean;
  renderAction?: (item: DataItem) => React.ReactNode;
  actionFixed?: boolean;
  headerFixed?: boolean;
  rowPerPage?: number;
  enableServerSidePagination?: boolean;
  fetchDataFromApi?: FetchDataFn;
  refreshFunc?: () => Promise<any>;
  refreshCountdown?: number;
  total?: number;
  pagination?: Pagination;
}

const DataTable: FC<DataTableProps> = forwardRef<DataTableRef, DataTableProps>(
  (
    {
      tableId,
      isLoading,
      columns: initialColumns,
      data,
      total,
      tfoot = [],
      enableFilter = true,
      enableManageColumn = true,
      enablePagination = true,
      rowPerPage = DEFAULT_PAGE_SIZE,
      renderAction,
      actionFixed = false,
      enableServerSidePagination = false,
      fetchDataFromApi,
      refreshFunc,
      refreshCountdown = DEFAULT_REFRESH_COUNTDOWN,
      headerFixed = false,
      pagination,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    // State management
    const [columns, setColumns] = useState(initialColumns);
    const [appliedFilters, setAppliedFilters] = useState<FilterItem[]>([]);
    const [sort, setSort] = useState<SortConfig>({ column: '', direction: '' });
    const [offcanvasFilters, setOffcanvasFilters] = useState<
      Record<string, string>
    >({});
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(rowPerPage);
    const [remoteData, setRemoteData] = useState<DataItem[]>([]);
    const [totalItems, setTotalItems] = useState(total | data.length);
    const [loading, setLoading] = useState(false);
    const [isColumnModified, setIsColumnModified] = useState(false);

    // Auto-refresh state
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
    const [countdown, setCountdown] = useState(refreshCountdown);
    const [initCountdown, setInitCountdown] = useState(refreshCountdown);

    // Refs for sticky columns
    const thRefs = useRef<HTMLTableHeaderCellElement[]>([]);
    const thRefsAction = useRef<HTMLTableHeaderCellElement>();
    const stickyOffsets = useStickyOffsets(
      columns,
      thRefs,
      thRefsAction,
      actionFixed,
      data,
    );

    const handleChangeFilter = useCallback((value : Record<string, string>) => {
      setCurrentPage(1);
      removeQueries([pagination?.pageKey || PAGE_KEY]);
      setOffcanvasFilters(value);
    }, [])

    // Data fetching function
    const refetchData = () => {
      if (enableServerSidePagination) {
        if (fetchDataFromApi) {

          fetchDataFromApi(
            currentPage - 1,
            limit,
            appliedFilters,
            sort.column,
            sort.direction,
          ).then((res) => {
            setRemoteData(res.items);
            setTotalItems(res.total);
            setLoading(false);
          });
        }
      }
    };

    // Expose refetchData to parent component
    useImperativeHandle(ref, () => ({
      refetchData,
    }));

    useEffect(() => {
      if (!enableManageColumn) {
        localStorage.removeItem(`${tableId}${LOCAL_STORAGE_KEYS.IS_HIDE}`);
        localStorage.removeItem(`${tableId}${LOCAL_STORAGE_KEYS.COLUMN_ORDER}`);
      }

      const orderedColumns = loadColumnOrder(initialColumns, tableId);
      const visibleColumns = loadColumnVisibility(orderedColumns, tableId);
      setColumns(visibleColumns);
      
    }, [tableId, enableManageColumn, rowPerPage, initialColumns]);

    // Load filters from URL
    useEffect(() => {
      const searchParams = new URLSearchParams(window.location.search);
      const filtersFromQuery: Record<string, string> = {};

      let sort: SortConfig = { column: '', direction: '' };
      searchParams.forEach((value, key) => {
        if (key === SORT_KEY) {
          sort.column = value;
        } else if (key === ORDER_KEY && (value == 'asc' || value == 'desc')) {
          sort.direction = value;
        } else if (key === (pagination?.pageKey || PAGE_KEY)) {
          setCurrentPage(parseInt(value));
        } else if (key === (pagination?.pageSizeKey || PAGE_SIZE_KEY) ) {
          setLimit(parseInt(value));
        } else if (columns.find((col) => col.filterKey === key || col.key === key)) {
          filtersFromQuery[key] = value;
        }
      });

      if (sort.column && sort.direction) {
        setSort(sort);
      }

      if (Object.keys(filtersFromQuery).length > 0) {
        setOffcanvasFilters(filtersFromQuery);
        const arr = Object.entries(filtersFromQuery).map(([k, v]) => ({
          key: k,
          value: v,
        }));
        setAppliedFilters(arr);
      }
    }, [pagination]);

    // Update data when props change
    useEffect(() => {
      if (total != undefined) {
        setTotalItems(total);
        setRemoteData(data);
      }
      if (!enableServerSidePagination) {
        setTotalItems(data.length);
      } else if (data && data.length > 0) {
        setRemoteData(data);
      }
    }, [data, total, enableServerSidePagination]);

    // Fetch data when filters/pagination change
    useEffect(() => {
      refetchData();
    }, [currentPage, limit, appliedFilters, sort]);

    // Track column modifications
    useEffect(() => {
      const isModified =
        columns.length !== initialColumns.length ||
        columns.some(
          (col, idx) =>
            col.key !== initialColumns[idx].key ||
            col.isHide !== initialColumns[idx].isHide,
        );
      setIsColumnModified(isModified);
    }, [columns, initialColumns]);

    // Auto-refresh countdown
    useEffect(() => {
      let intervalId: number | undefined;

      if (enableServerSidePagination && autoRefreshEnabled) {
        intervalId = setInterval(() => {
          setCountdown((prev) => prev - 1);
        }, 1000);
      }

      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    }, [enableServerSidePagination, autoRefreshEnabled]);

    // Trigger refresh when countdown reaches zero
    useEffect(() => {
      if (countdown === 0 && autoRefreshEnabled && enableServerSidePagination) {
        setCountdown(initCountdown);
        if (!!refreshFunc) {
          refreshFunc()
        } else {
          refetchData();
        }
      }
    }, [
      countdown,
      autoRefreshEnabled,
      enableServerSidePagination,
      initCountdown,
      refreshFunc,
    ]);

    // Process data for client-side pagination
    const { pagedData } = useDataProcessing({
      data,
      columns,
      appliedFilters,
      sortedColumn: sort.column,
      sortDirection: sort.direction,
      rowsPerPage: limit,
      currentPage,
    });

    const tableData = enableServerSidePagination ? remoteData : pagedData;

    // Event handlers
    const handleClearFilters = () => {
      setAppliedFilters([]);
      setOffcanvasFilters({});
      // localStorage.setItem(`${tableId}${LOCAL_STORAGE_KEYS.FILTERS}`, '[]');

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.forEach((_, key) => searchParams.delete(key));
      window.history.replaceState({}, '', window.location.pathname);
      setCurrentPage(1);
      removeQueries([pagination?.pageKey || PAGE_KEY]);
    };

    const handleRowsPerPageChange = (value: number) => {
      // localStorage.setItem(
      //   `${tableId}${LOCAL_STORAGE_KEYS.ROWS_PER_PAGE}`,
      //   String(value),
      // );
      setLimit(value);
      setCurrentPage(1);
      pushQueries({
        [pagination?.pageSizeKey || PAGE_SIZE_KEY]: value.toString(),
      });
      removeQueries([pagination?.pageKey || PAGE_KEY]);
    };

    const handleAutoRefreshToggle = () => {
      setCountdown(initCountdown);
      setAutoRefreshEnabled(!autoRefreshEnabled);
    };

    const handleResetColumns = () => {
      setColumns(initialColumns);
      localStorage.removeItem(`${tableId}${LOCAL_STORAGE_KEYS.IS_HIDE}`);
      localStorage.removeItem(`${tableId}${LOCAL_STORAGE_KEYS.COLUMN_ORDER}`);
    };

    return (
      <>
        {(enablePagination || enableFilter || enableManageColumn) && (
          <TableControls
            tableId={tableId}
            enablePagination={enablePagination}
            enableFilter={enableFilter}
            enableManageColumn={enableManageColumn}
            enableServerSidePagination={enableServerSidePagination}
            appliedFilters={appliedFilters}
            isColumnModified={isColumnModified}
            autoRefreshEnabled={autoRefreshEnabled}
            countdown={countdown}
            initCountdown={initCountdown}
            setInitCountdown={setInitCountdown}
            onClearFilters={handleClearFilters}
            onRowsPerPageChange={handleRowsPerPageChange}
            onAutoRefreshToggle={handleAutoRefreshToggle}
            rowsPerPage={limit}
          />
        )}

        <div
          className={classNames('table-responsive', {
            [styles.tableStickyHead]: headerFixed,
          })}
        >
          <table className="table card-table table-vcenter text-nowrap datatable">
            <TableHeader
              columns={columns}
              onToggleColumn={(key) =>
                handleToggleColumn(key, columns, setColumns, tableId)
              }
              onSort={(key) => handleSort(key, sort, setSort)}
              sort={sort}
              stickyOffsets={stickyOffsets}
              headerFixed={headerFixed}
              enableManageColumn={enableManageColumn}
              hasActions={!!renderAction}
            />
            <tbody className="table-tbody">
              {loading || isLoading ? (
                <LoadingRows
                  columnsCount={columns.length}
                  rowsCount={limit}
                  hasActions={!!renderAction}
                />
              ) : (
                <TableRows
                  tableData={tableData}
                  columns={columns}
                  renderAction={renderAction}
                  stickyOffsets={stickyOffsets}
                  actionFixed={actionFixed}
                />
              )}
            </tbody>
            {tfoot && <tfoot>{tfoot}</tfoot>}
          </table>
        </div>

        {enablePagination && (
          <DataPagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={limit}
            onPageChange={setCurrentPage}
            pagination={pagination}
          />
        )}

        {enableManageColumn && (
          <ManageColumnModal
            tableId={tableId}
            columns={columns}
            onToggleColumn={(key) =>
              handleToggleColumn(key, columns, setColumns, tableId)
            }
            onReorderColumns={(newColumns) =>
              handleReorderColumns(newColumns, setColumns, tableId)
            }
            onResetColumns={handleResetColumns}
          />
        )}

        {enableFilter && (
          <OffcanvasFilterPanel
            offcanvasFilters={offcanvasFilters}
            setOffcanvasFilters={handleChangeFilter}
            tableId={tableId}
            columns={columns}
            onApplyFilter={(key, value) =>
              handleApplyFilter(
                key,
                value,
                appliedFilters,
                setAppliedFilters,
                tableId,
              )
            }
          />
        )}
      </>
    );
  },
);

DataTable.displayName = 'DataTable';

export default DataTable;
